<template>
<Layout title="Proposal & Quotation" description="Easily generate accurate quotations and professional proposals automatically, saving time while ensuring consistency and transparency for every client.">
  <div>
    <div class="d-flex gap-2 mb-2" style="padding-left:24px">
      <button
        class="btn px-3 py-1 rounded-pill fw-semibold"
        :class="tab === 'quote' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
        @click="tab = 'quote'"
      >
        Quotation
      </button>
      <!-- <button
        class="btn px-3 py-1 rounded-pill fw-semibold"
        :class="tab === 'proposal' ? 'bg-white shadow-sm border' : 'bg-light text-muted border-0'"
        @click="tab = 'proposal'"
      >
        Proposal
      </button> -->
    </div>
  </div>
  
  <!-- ===== Quotations ===== -->
  <div v-if="tab=='quote'" class="mx-auto p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h1 class="text-3xl font-bold">Quotations</h1>
        <p class="text-muted-foreground">Manage your quotations and track their status</p>
      </div>
      <button 
        class="btn btn-primary d-flex align-items-center gap-2 px-3 py-2 text-sm"
        @click="openDialog()"
      >
        <Plus class="h-4 w-4" />
        <span class="d-none d-md-inline">New Quotation</span>
      </button>
    </div>

    <!-- Search -->
    <div class="mb-3">
      <input 
        v-model="searchQuote" 
        type="text" 
        placeholder="Search by Title or Client..." 
        class="form-control"
      />
    </div>
    <div v-if="isDialogOpen" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="createQuotation">
            <div class="modal-header">
              <h5 class="modal-title">Create New Quotation</h5>
              <button type="button" class="btn-close" @click="closeDialog"></button>
            </div>
            <div class="modal-body">
              <!-- Client Name -->
              <div class="mb-3">
                <label for="clientName" class="form-label">Client Name</label>
                <input
                  v-model="formData.clientName"
                  type="text"
                  class="form-control"
                  id="clientName"
                  required
                />
              </div>

              <!-- Quotation Title -->
              <div class="mb-3">
                <label for="title" class="form-label">Quotation Title</label>
                <input
                  v-model="formData.title"
                  type="text"
                  class="form-control"
                  id="title"
                  required
                />
              </div>

              <!-- Description -->
              <div class="mb-3">
                <label for="description" class="form-label">Description</label>
                <textarea
                  v-model="formData.description"
                  class="form-control"
                  id="description"
                  rows="4"
                  required
                ></textarea>
              </div>

              <!-- Amount -->
              <div class="mb-3">
                <label for="amount" class="form-label">Amount ($)</label>
                <input
                  v-model="formData.amount"
                  type="number"
                  step="0.01"
                  class="form-control"
                  id="amount"
                  required
                />
              </div>

              <!-- Payout Term -->
              <div class="mb-3">
                <label for="payoutTerm" class="form-label">Payout Term</label>
                <input
                  v-model="formData.payoutTerm"
                  type="text"
                  class="form-control"
                  id="payoutTerm"
                  placeholder="e.g. 30 days, 50% upfront"
                  required
                />
              </div>
            </div>
            <div class="modal-footer">
              <button class="btn btn-success d-flex align-items-center gap-2" type="submit">
                <i class="bi bi-file-earmark-text"></i>
                Generate Quotation
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
    <!-- Table -->
    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">All Quotations</h5>
      </div>
      <div class="card-body" style="max-height:400px; overflow-y:auto;">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>Quotation #</th>
              <th>Title</th>
              <th>Client</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="quotation in filteredQuotations" :key="quotation.docID">
              <td>{{ quotation.docID }}</td>
              <td>{{ quotation.Title }}</td>
              <td>{{ quotation.Client }}</td>
              <td style="max-width:300px; white-space:pre-wrap; word-break:break-word;">
                {{ quotation.Description }}
              </td>
              <td>${{ quotation.Amount != null ? quotation.Amount.toLocaleString() : '' }}</td>
              <td>{{ formatDate(quotation.createdAt) }}</td>
              <td>
                <div class="d-flex gap-2">
                  <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    @click="downloadQuotation(quotation.docID)">
                   <Download class="h-4 w-4" /> Download
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>

  <!-- ===== Proposals ===== -->
  <!-- <div v-if="tab=='proposal'" class="mx-auto p-6">
   
    <div class="flex justify-between items-center mb-4">
      <div>
        <h1 class="text-3xl font-bold">Proposals</h1>
        <p class="text-muted-foreground">Create and manage your business proposals</p>
      </div>
      <button class="btn btn-primary d-flex align-items-center gap-2" @click="openProposalDialog()">
        <Plus class="h-4 w-4" />
        New Proposal
      </button>
    </div>
    <div v-if="isProposalDialogOpen" class="modal fade show d-block" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <form @submit.prevent="handleProposalSubmit">
            <div class="modal-header">
              <h5 class="modal-title">
                {{ editingProposal ? "Edit Proposal" : "Create New Proposal" }}
              </h5>
              <button type="button" class="btn-close" @click="closeProposalDialog"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3">
                <label for="proposalClientName" class="form-label">Client Name</label>
                <input v-model="proposalForm.clientName" type="text" class="form-control" id="proposalClientName" required />
              </div>
              <div class="mb-3">
                <label for="proposalTitle" class="form-label">Proposal Title</label>
                <input v-model="proposalForm.title" type="text" class="form-control" id="proposalTitle" required />
              </div>
              <div class="mb-3">
                <label for="proposalDescription" class="form-label">Problem Statement</label>
                <textarea v-model="proposalForm.description" class="form-control" id="proposalDescription" rows="4" required></textarea>
              </div>
              
            </div>
            <div class="modal-footer">
              <button class="btn btn-success d-flex align-items-center gap-2" type="submit">
                <i class="bi bi-file-earmark-text"></i>
                {{ editingProposal ? "Update Proposal" : "Generate Proposal" }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <input 
        v-model="searchProposal" 
        type="text" 
        placeholder="Search by Title or Client..." 
        class="form-control"
      />
    </div>

    <div class="card">
      <div class="card-header">
        <h5 class="card-title mb-0">All Proposals</h5>
      </div>
      <div class="card-body" style="max-height:400px; overflow-y:auto;">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>Proposal #</th>
              <th>Title</th>
              <th>Client</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="proposal in filteredProposals" :key="proposal.id">
              <td>{{ proposal.proposalNumber }}</td>
              <td>{{ proposal.title }}</td>
              <td>{{ proposal.clientName }}</td>
              <td style="max-width:300px; white-space:pre-wrap; word-break:break-word;">
                {{ proposal.description }}
              </td>
              <td>
                <div class="d-flex gap-2">
                  <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                    @click="handleProposalDownload(proposal)">
                   <Download class="h-4 w-4" /> Download
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div> -->
</Layout>
</template>

<script>
import { Plus, Download } from "lucide-vue-next";
import Layout from '@/components/Layout.vue';
import { useToast } from 'vue-toastification'
import { getAuth, onAuthStateChanged } from "firebase/auth";
import router from "@/router";
import axios from "axios";

export default {
  name: "Quotations",
  components: { Layout, Plus, Download },
  data() {
    return {
      tab: "quote",
      userID:"",
      // apiURL: "http://127.0.0.1:5001",
      apiURL: "https://genstoryflow-backend-main-build-353838813175.europe-west1.run.app", 
      quotations: [],
      searchQuote: "",
      searchProposal: "",
      proposals: [],
      isDialogOpen: false,
      formData: {
        clientName: "",
        title: "",
        description: "",
        amount: "",
        payoutTerm: "",
      },
      isProposalDialogOpen: false,
      editingProposal: null,
      proposalForm: {
        title: "",
        description: "",
        estimatedValue: "",
        clientName: "",
        deadline: "",
      },
    };
  },
  computed: {
    filteredQuotations() {
      const q = this.searchQuote.toLowerCase();
      return this.quotations.filter(
        item =>
          item.docID.toLowerCase().includes(q) ||
          item.Title.toLowerCase().includes(q) ||
          item.Client.toLowerCase().includes(q)
      );
    },
    filteredProposals() {
      const q = this.searchProposal.toLowerCase();
      return this.proposals.filter(
        item =>
          item.title.toLowerCase().includes(q) ||
          item.clientName.toLowerCase().includes(q)
      );
    }
  },
  setup() {
    const toast = useToast()
    return { toast }
  },
  async mounted(){
    this.userID = this.$store.getters.getUserId;
    await this.checkAuthState();
    await this.fetchQuotations();
  },
  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser;
      if (!user) throw new Error('Not logged in');
      const token = await user.getIdToken();
      return { Authorization: token };
    },
    async checkAuthState() {
      const auth = getAuth();
      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async (user) => {
          if (user) {
            try {
              const idToken = await user.getIdToken();
              this.token = idToken;
              resolve();
            } catch (error) {
              reject(error);
            }
          } else {
            router.push("/");
            reject(new Error("No user logged in"));
          }
        });
      });
    },
    async createQuotation() {
      try {
        const headers = await this.getAuthHeader();
        await axios.post(
          `${this.apiURL}/api/quotation`,
          {
            clientName: this.formData.clientName,
            title: this.formData.title,
            description: this.formData.description,
            amount: this.formData.amount,
            paymentTerm: this.formData.paymentTerm
          },
          { headers }
        );
        this.toast.success("Quotation created successfully");
        await this.fetchQuotations();
        this.closeDialog();
      } catch (err) {
        console.error("Error creating quotation:", err);
        this.toast.error("Error creating quotation");
      }
    },
    async fetchQuotations() {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(`${this.apiURL}/api/quotations`, { headers });
        this.quotations = res.data;
      } catch (err) {
        console.error("Error fetching quotations:", err);
        this.toast.error("Unable to load quotations");
      }
    },
    async downloadQuotation(docId) {
      try {
        const headers = await this.getAuthHeader();
        const res = await axios.get(
          `${this.apiURL}/api/quotation/${docId}/download`,
          { headers, responseType: "blob" }
        );
        if (!res.data || res.data.size === 0) {
          this.toast.error("Quotation file is empty.");
          return;
        }
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `${docId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toast.success("Quotation downloaded ✅");
      } catch (err) {
        console.error("Download failed:", err);
        this.toast.error("Unable to download quotation");
      }
    },
    openDialog() { this.isDialogOpen = true; },
    closeDialog() {
      this.isDialogOpen = false;
      this.formData = { clientName: "", title: "", description: "", amount: "", payoutTerm: "" };
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString();
    },
    openProposalDialog() { this.isProposalDialogOpen = true; },
    closeProposalDialog() {
      this.isProposalDialogOpen = false;
      this.editingProposal = null;
      this.proposalForm = { title: "", description: "", estimatedValue: "", clientName: "", deadline: "" };
    },
    handleProposalDownload(proposal) {
      this.toast.success(`Downloading ${proposal.proposalNumber}.pdf`);
    },
  },
};
</script>

<style scoped>
.modal {
  background: rgba(0, 0, 0, 0.5);
}
</style>
